'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function CookiesPage() {
  const lastUpdated = new Date()?.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const cookies = [
    {
      name: 'buchhaltungaktas_consent_v1',
      category: 'Notwendig',
      purpose: 'Speichert Ihre Cookie-Einwilligungseinstellungen',
      duration: 'Bis zum Löschen',
    },
    {
      name: '_ga',
      category: 'Analytik',
      purpose: 'Google Analytics - Eindeutige Benutzer-ID',
      duration: '2 Jahre',
    },
    {
      name: '_gid',
      category: 'Analytik',
      purpose: 'Google Analytics - Session-ID',
      duration: '24 Stunden',
    },
    {
      name: '__cf_bm',
      category: 'Notwendig',
      purpose: 'Cloudflare Bot Management - Schutz vor Bots',
      duration: '30 Minuten',
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background)', color: 'var(--foreground)' }}>
      <Header />
      <main className="max-w-4xl mx-auto px-5 md:px-10 py-16 md:py-28">
        {/* Back link */}
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-sm font-medium mb-8 transition-colors min-h-[44px]"
          style={{ color: 'var(--muted-foreground)' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--foreground)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--muted-foreground)')}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Zurück zur Startseite
        </Link>

        {/* Page Title */}
        <h1 className="font-display font-semibold text-3xl md:text-5xl mb-4 leading-tight" style={{ color: 'var(--foreground)' }}>
          Cookie-Richtlinie
        </h1>
        <p className="text-sm mb-10" style={{ color: 'var(--muted-foreground)' }}>
          Zuletzt aktualisiert: {lastUpdated}
        </p>

        <div className="space-y-8 md:space-y-10 text-base leading-relaxed" style={{ color: 'var(--foreground)' }}>

          {/* 1. What Are Cookies */}
          <section>
            <h2 className="font-display font-semibold text-2xl mb-4" style={{ color: 'var(--foreground)' }}>
              1. Was sind Cookies?
            </h2>
            <p style={{ color: 'var(--muted-foreground)' }}>
              Cookies sind kleine Textdateien, die auf Ihrem Gerät gespeichert werden, wenn Sie unsere Website besuchen. Sie ermöglichen es uns, Ihre Präferenzen zu speichern, Ihre Erfahrung zu personalisieren und die Website-Nutzung zu analysieren. Cookies sind ein Standard-Bestandteil des Internets und werden von den meisten Websites verwendet.
            </p>
          </section>

          {/* 2. Cookies We Use */}
          <section>
            <h2 className="font-display font-semibold text-2xl mb-4" style={{ color: 'var(--foreground)' }}>
              2. Cookies, die wir verwenden
            </h2>
            <p style={{ color: 'var(--muted-foreground)' }} className="mb-4">
              Hier ist eine vollständige Liste der Cookies, die unsere Website verwendet:
            </p>
            <div className="overflow-x-auto -mx-5 px-5 md:mx-0 md:px-0">
              <table className="w-full border-collapse text-sm min-w-[480px]">
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th className="text-left p-3 font-semibold">Name</th>
                    <th className="text-left p-3 font-semibold">Kategorie</th>
                    <th className="text-left p-3 font-semibold">Zweck</th>
                    <th className="text-left p-3 font-semibold">Dauer</th>
                  </tr>
                </thead>
                <tbody>
                  {cookies?.map((cookie, index) => (
                    <tr
                      key={index}
                      style={{
                        borderBottom: '1px solid var(--border)',
                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)',
                      }}
                    >
                      <td className="p-3" style={{ color: 'var(--muted-foreground)' }}>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded" style={{ backgroundColor: 'var(--muted-foreground)', color: 'white', opacity: 0.1 }}>
                          {cookie?.name}
                        </code>
                      </td>
                      <td className="p-3" style={{ color: 'var(--muted-foreground)' }}>
                        {cookie?.category}
                      </td>
                      <td className="p-3" style={{ color: 'var(--muted-foreground)' }}>
                        {cookie?.purpose}
                      </td>
                      <td className="p-3" style={{ color: 'var(--muted-foreground)' }}>
                        {cookie?.duration}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* 3. How to Manage Cookies */}
          <section>
            <h2 className="font-display font-semibold text-2xl mb-4" style={{ color: 'var(--foreground)' }}>
              3. Wie Sie Cookies verwalten können
            </h2>
            <p style={{ color: 'var(--muted-foreground)' }} className="mb-3">
              Sie haben mehrere Möglichkeiten, Cookies zu verwalten:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2" style={{ color: 'var(--muted-foreground)' }}>
              <li>
                <strong>Cookie-Einstellungen auf unserer Website:</strong> Verwenden Sie unsere{' '}
                <button
                  onClick={() => {
                    const event = new CustomEvent('openCookiePreferences');
                    window.dispatchEvent(event);
                  }}
                  className="underline" style={{ color: 'var(--accent)' }}
                >
                  Cookie-Präferenzen
                </button>
                , um Ihre Einwilligungen zu verwalten.
              </li>
              <li>
                <strong>Browser-Einstellungen:</strong> Die meisten Browser ermöglichen es Ihnen, Cookies zu blockieren oder zu löschen. Konsultieren Sie die Hilfe Ihres Browsers für spezifische Anweisungen.
              </li>
              <li>
                <strong>Opt-out-Tools:</strong> Sie können sich von Tracking-Cookies abmelden, indem Sie die Opt-out-Optionen der jeweiligen Anbieter nutzen.
              </li>
            </ul>
            <p style={{ color: 'var(--muted-foreground)' }} className="mt-3">
              Bitte beachten Sie, dass das Blockieren von notwendigen Cookies die Funktionalität unserer Website beeinträchtigen kann.
            </p>
          </section>

          {/* 4. Last Updated */}
          <section>
            <h2 className="font-display font-semibold text-2xl mb-4" style={{ color: 'var(--foreground)' }}>
              4. Letzte Aktualisierung
            </h2>
            <p style={{ color: 'var(--muted-foreground)' }}>
              Diese Cookie-Richtlinie wurde zuletzt am {lastUpdated} aktualisiert. Wir behalten uns das Recht vor, diese Richtlinie jederzeit zu ändern. Änderungen werden auf dieser Seite veröffentlicht.
            </p>
          </section>

        </div>
      </main>
      <Footer />
    </div>
  );
}
